import React, { useMemo } from 'react';
import { DollarSign, TrendingDown, TrendingUp } from 'lucide-react';
import { MasterTransaction } from '../types';
import { isSubmitted, isAllocated, isSubmittedCash, isSubmittedJar, isSubmittedPOS, isDispute, isPendingFinalization, getPendingDisputeTotal } from '../lib/transactionUtils';
import { formatCurrency } from '../lib/formatters';

interface Props {
  transactions: MasterTransaction[];
  pendingDisputes?: any[];
}

export const ReconciliationSummaryBoxes: React.FC<Props> = ({ transactions, pendingDisputes = [] }) => {
  const { totalCashSubmitted, totalJarSubmitted, totalPOSSubmitted, totalSubmittedLedger } = useMemo(() => {
    let cash = 0, jar = 0, pos = 0, submittedLedger = 0;

    for (const tx of transactions) {
      const submittedAmount = tx.amount || tx.submittedAmount || 0;
      
      if (isSubmittedCash(tx, transactions)) cash += submittedAmount;
      if (isSubmittedJar(tx, transactions)) jar += submittedAmount;
      if (isSubmittedPOS(tx, transactions)) pos += submittedAmount;
      if (isSubmitted(tx, transactions) && tx.source !== 'Calculated') submittedLedger += submittedAmount;
    }

    return { totalCashSubmitted: cash, totalJarSubmitted: jar, totalPOSSubmitted: pos, totalSubmittedLedger: submittedLedger };
  }, [transactions]);

  const totalDisputeSubmitted = useMemo(() => getPendingDisputeTotal(pendingDisputes), [pendingDisputes]);
  const totalSubmitted = useMemo(() => totalSubmittedLedger + totalDisputeSubmitted, [totalSubmittedLedger, totalDisputeSubmitted]);
  const totalAllocated = useMemo(() => transactions
    .filter(tx => isAllocated(tx, transactions) && tx.source !== 'Calculated')
    .reduce((acc, tx) => acc + (tx.amount || tx.allocatedAmount || 0), 0), [transactions]);

  const totalPendingFinalization = useMemo(() => transactions
    .filter(isPendingFinalization)
    .reduce((acc, tx) => acc + (tx.amount || 0), 0), [transactions]);

  const totalAllocatedWithPending = totalAllocated + totalPendingFinalization;

  const variance = totalSubmitted - totalAllocated;

  return (
    <div className="flex flex-col gap-6 mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-6">
        <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
          <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Cash Submitted</h3>
          <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalCashSubmitted)}</p>
        </div>
        <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
          <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Jar Submitted</h3>
          <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalJarSubmitted)}</p>
        </div>
        <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
          <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">POS Submitted</h3>
          <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalPOSSubmitted)}</p>
        </div>
        <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
          <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Disputes Submitted</h3>
          <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalDisputeSubmitted)}</p>
        </div>
        <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
          <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Total Submitted</h3>
          <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalSubmitted)}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-100/50">
          <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">Allocated Tips</h3>
          <p className="text-2xl font-bold text-emerald-900">{formatCurrency(totalAllocated)}</p>
        </div>
        <div className={`p-4 rounded-2xl border flex items-center justify-between ${
          totalPendingFinalization !== 0 ? 'bg-amber-50/50 border-amber-100/50' : 'bg-green-50/50 border-green-100/50'
        }`}>
          <div>
            <h3 className={`text-xs font-bold uppercase tracking-wider mb-1 ${
              totalPendingFinalization !== 0 ? 'text-amber-600' : 'text-green-600'
            }`}>Pending Finalization</h3>
            <p className={`text-2xl font-bold ${
              totalPendingFinalization !== 0 ? 'text-amber-900' : 'text-green-900'
            }`}>{formatCurrency(totalPendingFinalization)}</p>
          </div>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            totalPendingFinalization !== 0 ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'
          }`}>
            {totalPendingFinalization > 0 ? <TrendingDown size={20} /> : totalPendingFinalization < 0 ? <TrendingUp size={20} /> : <DollarSign size={20} />}
          </div>
        </div>
        <div className="p-4 bg-purple-50/50 rounded-2xl border border-purple-100/50">
          <h3 className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1">Total Allocated (w/ Pending)</h3>
          <p className="text-2xl font-bold text-purple-900">{formatCurrency(totalAllocatedWithPending)}</p>
        </div>
      </div>
    </div>
  );
};
